/** Bond Predict (Polymarket V2) — filters, formatting, market display helpers */

import { formatAppDateTime } from "./appTimeFormat";
import { sortLeaguesByPriority } from "./leaguePriority";

export const MARKET_TYPES = {
  BINARY: "BINARY",
  MULTI_BINARY: "MULTI_BINARY",
  HEAD_TO_HEAD: "HEAD_TO_HEAD",
};

export const STATUS_FILTERS = [
  { value: "", label: "All markets", hint: "Every contract" },
  { value: "OPEN", label: "Live", hint: "Open for trading" },
  { value: "CLOSED", label: "Closed", hint: "Awaiting outcome" },
  { value: "RESOLVED", label: "Settling", hint: "Outcome pending" },
  { value: "FINALIZED", label: "Settled", hint: "Final payout · 24h" },
  { value: "VOID", label: "Voided", hint: "Cancelled · 24h" },
  { value: "ON_HOLD", label: "On hold", hint: "Paused markets" },
];

export const TYPE_FILTERS = [
  { value: "", label: "All types", hint: "Any market shape" },
  { value: "BINARY", label: "Yes / No", hint: "Binary contracts" },
  { value: "MULTI_BINARY", label: "Multi-outcome", hint: "Several lines" },
  { value: "HEAD_TO_HEAD", label: "Head to head", hint: "Side vs side" },
];

export const CATEGORY_FILTERS = [
  { value: "", label: "All categories", hint: "Every sport & topic" },
  { value: "FOOTBALL", label: "Football", hint: "Soccer markets" },
  { value: "BASKETBALL", label: "Basketball", hint: "NBA & hoops" },
  { value: "AMERICAN_FOOTBALL", label: "NFL", hint: "American football" },
  { value: "CRYPTO", label: "Crypto", hint: "Token & price calls" },
  { value: "POLITICS", label: "Politics", hint: "Elections & policy" },
  { value: "ENTERTAINMENT", label: "Entertainment", hint: "Culture & awards" },
  { value: "ESPORTS", label: "Esports", hint: "Competitive gaming" },
  { value: "TECH", label: "Tech", hint: "Product & launches" },
];

export const SORT_OPTIONS = [
  { value: "newest", label: "Newest", hint: "Recently listed", sortBy: "createdAt", sortOrder: "desc" },
  { value: "volume", label: "Volume", hint: "Highest liquidity", sortBy: "metrics.totalVolume", sortOrder: "desc" },
  { value: "closing", label: "Closing soon", hint: "Ending soonest", sortBy: "timings.closeAt", sortOrder: "asc" },
  { value: "activity", label: "Most active", hint: "Most trades", sortBy: "metrics.totalTrades", sortOrder: "desc" },
];

export const STATUS_META = {
  OPEN: { label: "Live", tone: "live" },
  CLOSED: { label: "Closed", tone: "closed" },
  RESOLVED: { label: "Settling", tone: "pending" },
  FINALIZED: { label: "Settled", tone: "final" },
  VOID: { label: "Void", tone: "closed" },
  REFUNDED: { label: "Refunded", tone: "muted" },
  ON_HOLD: { label: "Hold", tone: "pending" },
};

/** Terminal markets stay on the public board this long after resolution. */
export const MARKETS_RESOLVED_RETENTION_HOURS = 24;

const MARKETS_TERMINAL_STATUSES = new Set(["FINALIZED", "REFUNDED", "VOID"]);

/** Drop settled / voided / refunded markets older than the public retention window. */
export const isMarketWithinPublicRetention = (market, nowMs = Date.now()) => {
  const status = String(market?.status || "").toUpperCase();
  if (!MARKETS_TERMINAL_STATUSES.has(status)) return true;

  const floorMs = nowMs - MARKETS_RESOLVED_RETENTION_HOURS * 60 * 60 * 1000;
  const toMs = (value) => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  if (status === "FINALIZED") {
    const ts = toMs(market?.resolution?.resolvedAt) ?? toMs(market?.updatedAt);
    return ts != null && ts >= floorMs;
  }
  if (status === "REFUNDED") {
    const ts = toMs(market?.resolution?.refundedAt) ?? toMs(market?.updatedAt);
    return ts != null && ts >= floorMs;
  }
  const ts = toMs(market?.updatedAt) ?? toMs(market?.resolution?.resolvedAt);
  return ts != null && ts >= floorMs;
};

export const TYPE_META = {
  BINARY: { label: "Yes / No", short: "Binary" },
  MULTI_BINARY: { label: "Multi-outcome", short: "Multi" },
  HEAD_TO_HEAD: { label: "Head to head", short: "H2H" },
};

export const formatVolume = (num) => {
  const n = Number(num) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
};

/** Numeric chance 0–100 for bars / tape; null when no price. */
export const chancePctNumber = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(100, n * 100);
};

/**
 * User-facing chance from a 0–1 price.
 * Unknown or &lt;1% → "&lt;1%" (never "—" or "0%").
 */
export const formatChancePct = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return "<1%";
  const pct = Math.min(100, n * 100);
  if (pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
};

/** @deprecated Use formatChancePct */
export const formatPrice = formatChancePct;

export const getMarketTitle = (market) =>
  market?.question || market?.title || "Untitled market";

export {
  getMarketHeroImageUrl,
  getContractLineImageUrl,
  findContractLineByKey,
  subjectFromMarket,
  subjectFromContractLine,
} from "../../utils/marketSubjectDisplay";

export const getOutcomeDisplayPrice = (outcome) => {
  const p = outcome?.displayPrice;
  if (typeof p === "number") return p;
  const last = outcome?.metrics?.lastTradePrice;
  return typeof last === "number" ? last : null;
};

export const groupOutcomesBySelection = (outcomes = []) => {
  const map = {};
  outcomes.forEach((o) => {
    if (!o) return;
    const key = o.selectionKey || o._id || "";
    if (!key) return;
    if (!map[key]) {
      map[key] = { selectionKey: key, yes: null, no: null, direct: null };
    }
    if (o.side === "YES") map[key].yes = o;
    else if (o.side === "NO") map[key].no = o;
    else if (o.side === "DIRECT") map[key].direct = o;
  });
  return Object.values(map).sort((a, b) => {
    const aIdx = a.yes?.ordinalIndex ?? a.no?.ordinalIndex ?? a.direct?.ordinalIndex ?? 0;
    const bIdx = b.yes?.ordinalIndex ?? b.no?.ordinalIndex ?? b.direct?.ordinalIndex ?? 0;
    return aIdx - bIdx;
  });
};

export const formatShortCountdown = (target, lineStatus = "OPEN") => {
  if (!target) return "";
  if (lineStatus && lineStatus !== "OPEN") return "";
  const t = new Date(target).getTime();
  if (!Number.isFinite(t)) return "";
  const ms = t - Date.now();
  if (ms <= 0) return "";
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 60) return `${m}m left`;
  if (h < 24) return `${h}h ${m % 60}m`;
  if (d < 3) return `${d}d ${h % 24}h`;
  return formatAppDateTime(target);
};

export const getBinaryOdds = (market) => {
  const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : [];
  const yes = outcomes.find((o) => o.side === "YES" || o.label === "Yes");
  const no = outcomes.find((o) => o.side === "NO" || o.label === "No");
  const yesPrice = getOutcomeDisplayPrice(yes);
  const noPrice = getOutcomeDisplayPrice(no);
  const yesPct =
    chancePctNumber(yesPrice) != null
      ? Math.max(1, Math.round(chancePctNumber(yesPrice)))
      : null;
  const noPct =
    chancePctNumber(noPrice) != null
      ? Math.max(1, Math.round(chancePctNumber(noPrice)))
      : null;
  return { yes, no, yesPct, noPct, yesPrice, noPrice };
};

export const getH2HLabels = (market) => {
  const grouped = groupOutcomesBySelection(market?.outcomes || []);
  const g = grouped.find((x) => x.yes && x.no);
  if (!g) return null;
  const home = market?.fixture?.teams?.home;
  const away = market?.fixture?.teams?.away;
  return {
    labelA: home?.name || g.yes?.label || "Side A",
    labelB: away?.name || g.no?.label || "Side B",
    logoA: home?.logo || null,
    logoB: away?.logo || null,
    priceA: getOutcomeDisplayPrice(g.yes),
    priceB: getOutcomeDisplayPrice(g.no),
    fxStatus: market?.fixture?.status?.short || "VS",
  };
};

export const getMultiPreview = (market, limit = 4) => {
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter((g) => g.yes || g.no);
  return grouped.slice(0, limit).map((g) => {
    const label = (g.yes?.label || g.no?.label || g.selectionKey || "").replace(
      /\s*\((Yes|No)\)\s*$/i,
      ""
    );
    return {
      key: g.selectionKey,
      label,
      yesPct: getOutcomeDisplayPrice(g.yes),
    };
  });
};

export const sumMarketVolume = (markets = []) =>
  markets.reduce((sum, m) => sum + (Number(m?.metrics?.totalVolume) || 0), 0);

export const countOpenMarkets = (markets = []) =>
  markets.filter((m) => m?.status === "OPEN").length;

/** Leagues from fixture-linked markets (desk filter options). */
export const extractLeaguesFromMarkets = (markets = []) => {
  const map = new Map();
  markets.forEach((market) => {
    const league = market?.fixture?.league;
    const id = league?._id || league?.id;
    if (!id || !league?.name) return;
    const key = String(id);
    const prev = map.get(key);
    map.set(key, {
      id: key,
      apiId: league?.id ?? null,
      name: league.name,
      logo: league.logo || null,
      marketCount: (prev?.marketCount || 0) + 1,
    });
  });
  return sortLeaguesByPriority(Array.from(map.values()), {
    countKey: "marketCount",
  });
};

export const mergeLeagueOptions = (apiLeagues = [], marketLeagues = []) => {
  const map = new Map();
  [...apiLeagues, ...marketLeagues].forEach((lg) => {
    if (!lg?.id || !lg?.name) return;
    const key = String(lg.id);
    const prev = map.get(key);
    map.set(key, {
      id: key,
      apiId: lg.apiId ?? lg.id ?? prev?.apiId ?? null,
      name: lg.name,
      logo: lg.logo || prev?.logo || null,
      marketCount: Math.max(Number(lg.marketCount) || 0, Number(prev?.marketCount) || 0),
    });
  });
  return sortLeaguesByPriority(Array.from(map.values()), {
    countKey: "marketCount",
  });
};

export const marketMatchesLeague = (market, leagueId) => {
  if (!leagueId) return true;
  const league = market?.fixture?.league;
  const id = league?._id || league?.id;
  return id != null && String(id) === String(leagueId);
};

export const clientFilterMarkets = (markets, search) => {
  const retained = (markets || []).filter((m) => isMarketWithinPublicRetention(m));
  const q = (search || "").trim().toLowerCase();
  if (!q) return retained;
  return retained.filter((m) => {
    const title = getMarketTitle(m).toLowerCase();
    const desc = (m?.description || "").toLowerCase();
    const cat = (m?.category || "").toLowerCase();
    const home = m?.fixture?.teams?.home?.name?.toLowerCase() || "";
    const away = m?.fixture?.teams?.away?.name?.toLowerCase() || "";
    const league = (m?.fixture?.league?.name || "").toLowerCase();
    return (
      title.includes(q) ||
      desc.includes(q) ||
      cat.includes(q) ||
      home.includes(q) ||
      away.includes(q) ||
      league.includes(q)
    );
  });
};

export const sortMarketsClient = (markets, sortKey) => {
  const list = [...markets];
  switch (sortKey) {
    case "volume":
      return list.sort(
        (a, b) =>
          (Number(b?.metrics?.totalVolume) || 0) - (Number(a?.metrics?.totalVolume) || 0)
      );
    case "closing":
      return list.sort((a, b) => {
        const ta = new Date(a?.timings?.closeAt || 0).getTime();
        const tb = new Date(b?.timings?.closeAt || 0).getTime();
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return ta - tb;
      });
    case "activity":
      return list.sort(
        (a, b) =>
          (Number(b?.metrics?.totalTrades) || 0) - (Number(a?.metrics?.totalTrades) || 0)
      );
    case "newest":
    default:
      return list.sort((a, b) => {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
      });
  }
};

export const isMarketLikedByUser = (market, userId) => {
  if (!userId || !market?.likes) return false;
  const users = market.likes.users;
  if (!Array.isArray(users)) return false;
  return users.some((u) => {
    const id = typeof u === "string" ? u : u?._id;
    return id != null && String(id) === String(userId);
  });
};

/** Optimistic like toggle for instant UI feedback before API responds. */
export const applyOptimisticLike = (market, userId, currentlyLiked) => {
  if (!market || !userId) return market;
  const likes = market.likes || { count: 0, users: [] };
  const users = Array.isArray(likes.users) ? [...likes.users] : [];
  let count = Number(likes.count) || 0;

  const userInList = users.some((u) => {
    const id = typeof u === "string" ? u : u?._id;
    return id != null && String(id) === String(userId);
  });

  if (currentlyLiked) {
    return {
      ...market,
      likes: {
        count: Math.max(0, count - (userInList ? 1 : 0)),
        users: users.filter((u) => {
          const id = typeof u === "string" ? u : u?._id;
          return id == null || String(id) !== String(userId);
        }),
      },
    };
  }

  if (userInList) return market;
  return {
    ...market,
    likes: {
      count: count + 1,
      users: [...users, userId],
    },
  };
};

export const patchMarketInList = (list, updated) => {
  if (!updated?._id || !Array.isArray(list)) return list;
  const id = String(updated._id);
  return list.map((m) => (String(m._id) === id ? { ...m, ...updated } : m));
};

export const buildFetchParams = ({
  status,
  type,
  category,
  league,
  search,
  sortKey,
  limit = 48,
  skip = 0,
}) => {
  const sort = SORT_OPTIONS.find((o) => o.value === sortKey) || SORT_OPTIONS[0];
  const params = {
    limit,
    skip,
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
  };
  if (status) params.status = status;
  if (type) params.type = type;
  if (category) params.category = category;
  if (league) params.league = league;
  if (search?.trim()) params.search = search.trim();
  return params;
};
